export const servicesData = {
  'custom-software': {
    id: 'custom-software',
    title: 'Custom Software Development',
    icon: 'Code',
    tagline: 'Tailored digital systems built to solve your unique operational bottlenecks.',
    overview: 'Generic off-the-shelf software often forces you to adapt your workflows to their rigid structures. We do the opposite. We build custom management software, CRMs, ERPs, billing applications, and booking systems that map perfectly to how your business actually runs. By focusing on your specific needs, we eliminate bloated features you do not use and deliver a clean, fast experience.',
    problems: [
      'Over-reliance on disjointed, error-prone spreadsheets that require manual entry and lack real-time sync.',
      'Paying high monthly subscription fees for heavy software suites when you only use 10% of their features.',
      'Information silos where different parts of your business (sales, stock, billing) do not communicate with each other.',
      'Manual, repetitive tasks like scheduling, report generation, and status tracking consuming hours of your week.'
    ],
    solutions: [
      'We design and build consolidated dashboards that centralize your business metrics, billing details, and data streams.',
      'We replace monthly SaaS fees with a one-time build cost, giving you full ownership of your data and tools.',
      'We write custom logic that automates invoicing, attendance logs, and client bookings based on your rules.',
      'We integrate your existing software with new, lightweight databases to ensure seamless, real-time sync.'
    ],
    benefits: [
      'Save up to 10+ hours per week by automating repetitive record-keeping and updates.',
      'Zero ongoing software licensing costs—pay for the development once, own the code forever.',
      'Significantly reduced human error through validation and automated data sync.',
      'A system that scales smoothly as your client base and transaction volumes grow.'
    ],
    features: [
      'Custom CRM & ERP Modules: Track customer interactions, order history, and business metrics.',
      'Automated Billing & Invoicing: Auto-generate professional invoices and track unpaid balances.',
      'Dedicated Appointment Booking: Simple calendars for clients to book slots, with email/SMS reminders.',
      'Role-Based Access Control: Secure logins for staff, managers, and admins with custom permissions.'
    ],
    suitableIndustries: ['healthcare', 'retail', 'education', 'restaurant', 'hotel', 'real-estate', 'construction', 'manufacturing', 'travel', 'professional-services']
  },
  'web-apps': {
    id: 'web-apps',
    title: 'Web Applications & Portals',
    icon: 'Monitor',
    tagline: 'High-performance customer portals, dashboards, and custom business tools.',
    overview: 'Engage your clients and empower your internal team with fast, interactive web applications. We build secure customer portals, administrative dashboards, and internal databases that work flawlessly across desktops, tablets, and phones. Using technologies like React and Next.js, we deliver lightning-fast load times and smooth interfaces that keep users engaged.',
    problems: [
      'Clients calling or emailing constantly for simple updates, invoice copies, or project status tracking.',
      'Internal staff struggling to access business data securely when working remotely or in the field.',
      'Outdated web portals that feel slow, look unprofessional, and do not work well on mobile devices.',
      'A lack of central analytics that makes it hard to track user actions or customer retention.'
    ],
    solutions: [
      'We build self-service customer portals where clients can download invoices, view project updates, and submit requests.',
      'We create responsive, secure admin dashboards with clean visual charts showing your business health.',
      'We deploy cloud-hosted, mobile-friendly portals for on-the-field employees to log data instantly.',
      'We build quick, interactive search tools so users can find specific records in seconds.'
    ],
    benefits: [
      'Reduce client-support calls by up to 40% by giving customers self-service portal access.',
      'Real-time business insights through centralized, interactive dashboards and visual charts.',
      'Higher user satisfaction and conversion rates thanks to fast, mobile-friendly layouts.',
      'Secure, encrypted data hosting that keeps business and client records safe.'
    ],
    features: [
      'Self-Service Client Portals: Secure login area for clients to manage accounts, files, and payments.',
      'Interactive Admin Dashboards: Real-time visual data displays with downloadable CSV/PDF reports.',
      'Secure Authentication: Multi-factor login, social logins, and passwordless authentication.',
      'Instant Notifications: In-app alerts, email updates, and SMS notifications for user activity.'
    ],
    suitableIndustries: ['retail', 'education', 'real-estate', 'manufacturing', 'travel', 'professional-services', 'healthcare']
  },
  'mobile-apps': {
    id: 'mobile-apps',
    title: 'Mobile App Development',
    icon: 'Smartphone',
    tagline: 'High-performance iOS and Android apps built with React Native and Flutter.',
    overview: 'Get your business onto your customers home screens. We design and build cross-platform mobile apps for iOS and Android using React Native and Flutter. This approach allows us to write a single codebase that works beautifully on both platforms, saving you development costs while ensuring native-like speed, smooth animations, and offline functionality.',
    problems: [
      'Struggling to retain customers because you lack a direct, push-notification-enabled channel.',
      'Mobile websites that feel clunky, load slowly, or do not offer a smooth checkout/booking flow.',
      'Needing to build separate iOS and Android apps, which doubles your budget and maintenance efforts.',
      'The inability to access device hardware (like camera, GPS, or bluetooth) via standard web pages.'
    ],
    solutions: [
      'We build a single React Native or Flutter app that targets both Apple App Store and Google Play Store.',
      'We design offline-first architectures so your app works reliably even in low-connectivity areas.',
      'We integrate push notifications to help you run promotions, announce updates, and send reminders.',
      'We connect mobile apps directly to your device camera (for scanning codes) and GPS (for tracking locations).'
    ],
    benefits: [
      'Reach iOS and Android users simultaneously with a single, highly efficient codebase.',
      'Increase customer engagement by using direct, personalized push notifications.',
      'Improve speed-to-market and lower development costs compared to native coding.',
      'Robust offline capability ensuring users can access key information anytime.'
    ],
    features: [
      'Cross-Platform Build: Native-performance apps for both iOS and Android platforms.',
      'Offline Data Storage: Access key information and log changes without an internet connection.',
      'Push Notifications: Scheduled or real-time user notifications via Firebase Cloud Messaging.',
      'Device Integration: Seamless connection to cameras, GPS trackers, biometrics (Face ID), and Bluetooth.'
    ],
    suitableIndustries: ['retail', 'healthcare', 'restaurant', 'hotel', 'travel', 'fitness', 'beauty']
  },
  'ai-automation': {
    id: 'ai-automation',
    title: 'AI & Business Automation',
    icon: 'Bot',
    tagline: 'Automate repetitive workflows and build smart, conversational chatbots.',
    overview: 'Running a business involves countless repetitive tasks that drain your time. We build lightweight automation systems that handle lead routing, WhatsApp notifications, and customer inquiries automatically. By integrating AI models and automation APIs, we help you respond to clients instantly 24/7 without adding headcount, freeing you up to focus on growing your business.',
    problems: [
      'Losing potential clients because you cannot respond to inquiries immediately outside business hours.',
      'Spending hours every day copying and pasting messages into WhatsApp or manually sending billing reminders.',
      'Leads slipping through the cracks because there is no automated routing or follow-up sequence.',
      'Struggling to analyze client emails, support tickets, or feedback at scale.'
    ],
    solutions: [
      'We build custom AI chatbots (using OpenAI or Groq API) that handle common client questions on your site.',
      'We set up WhatsApp Business automation to send instant purchase receipts, booking confirmations, and shipping alerts.',
      'We construct automated workflows using API triggers to sync contact info between your website and CRM.',
      'We deploy smart email auto-responders that scan incoming emails and route them to the right team member.'
    ],
    benefits: [
      'Never miss a lead: instantly capture and respond to inquiries 24/7.',
      'Save hours of manual effort by automating invoice alerts and booking reminders via WhatsApp.',
      'Consistent, friendly customer support that answers questions in seconds.',
      'Reduced software overhead by building lean, direct API connections.'
    ],
    features: [
      'Custom Website Chatbots: Smart AI assistants trained on your specific business details.',
      'WhatsApp API Integration: Automated text confirmations, reminders, and customer support flows.',
      'Workflow Automation: API-based connectors that link forms, CRMs, and email tools.',
      'Smart Lead Routing: Automatically score and direct incoming inquiries to specific staff.'
    ],
    suitableIndustries: ['retail', 'hotel', 'restaurant', 'real-estate', 'travel', 'professional-services', 'healthcare']
  },
  'website-seo': {
    id: 'website-seo',
    title: 'Website Development & SEO',
    icon: 'Globe',
    tagline: 'Stunning websites optimized for Google search and modern AI answers.',
    overview: 'A beautiful website is useless if your customers cannot find it. We build custom marketing websites, corporate landing pages, and portfolio showcases that look modern, load in milliseconds, and rank highly. We optimize for search engine rankings (SEO), but we also prepare your business for modern AI search engines (like ChatGPT and Perplexity) using Answer Engine Optimization (AEO) and Generative Engine Optimization (GEO).',
    problems: [
      'A slow, outdated website that causes potential customers to leave before it even loads.',
      'Your business is invisible on Google Maps and search results when local customers look for your services.',
      'Your site looks broken on mobile devices, leading to lost sales and poor search rankings.',
      'Struggling to rank or appear in AI answers (like Google Gemini or Perplexity) when clients ask for local recommendations.'
    ],
    solutions: [
      'We develop modern, responsive websites using static site generation for instant load times.',
      'We set up and optimize your Google Business Profile to boost your rankings in local maps.',
      'We embed structured Schema markup to make your business easily readable by Google and AI engines.',
      'We write clean, semantic HTML5 code that search engines and screen readers can easily crawl.'
    ],
    benefits: [
      'Higher ranking on local Google Search and Google Maps for relevant searches.',
      'Better visibility in conversational AI search tools (AEO and GEO optimizations).',
      'Increased conversion rates from visitors who enjoy a fast, smooth mobile experience.',
      'Complete ownership of a website built without complex, slow website builders.'
    ],
    features: [
      'Responsive Web Design: Layouts that adapt perfectly to mobile, tablet, and desktop screens.',
      'Local SEO & Maps Optimization: Setup and strategic targeting for your local market.',
      'AI-Ready Schema Markup: Structured data code that helps Google and AI engines understand your content.',
      'Fast Static Deployment: Hosting on global networks (like Vercel or Netlify) for sub-second loading.'
    ],
    suitableIndustries: ['real-estate', 'restaurant', 'hotel', 'construction', 'healthcare', 'professional-services', 'retail', 'travel']
  },
  'design-branding': {
    id: 'design-branding',
    title: 'UI/UX Design & Branding',
    icon: 'Palette',
    tagline: 'Modern logo design, brand systems, and intuitive user interfaces.',
    overview: 'Trust begins with design. Before we write a single line of code, we design interfaces that look premium, professional, and are incredibly simple for your customers to navigate. We also build consistent brand identities, from clean logos to professional brochures and digital templates. We focus on a simple, human design style that represents your values honestly.',
    problems: [
      'Your current branding looks outdated, inconsistent, or like it was quickly generated by generic AI.',
      'Users get confused on your current website and leave because they cannot find the "Contact" or "Book" button.',
      'A lack of consistent brand colors, fonts, and assets makes your marketing look unprofessional.',
      'Digital products that look great on desktop but are hard to click or read on small phone screens.'
    ],
    solutions: [
      'We design interactive high-fidelity wireframes in Figma so you can click through your app before development.',
      'We create clean, recognizable logos and color systems that stand out in your industry.',
      'We build comprehensive design guidelines containing all the fonts, colors, and assets your team needs.',
      'We optimize button sizing, form layouts, and spacing to maximize user actions on mobile screens.'
    ],
    benefits: [
      'Avoid costly design reworks by validating interactive layouts in Figma first.',
      'Build immediate trust with prospective clients through a clean, modern brand identity.',
      'Boost form completions and checkout rates with simple, clear layout designs.',
      'Consistent design assets that look premium across your website, socials, and print.'
    ],
    features: [
      'Interactive Figma Prototyping: Clickable mockups of your app showing exact button clicks and transitions.',
      'Brand Identity Systems: Custom logo files, curated color palettes, and modern typography guides.',
      'Mobile-First Interface Design: Responsive layouts built for finger-friendly clicking.',
      'Print-Ready Assets: Business cards, brochures, and digital templates designed for consistency.'
    ],
    suitableIndustries: ['retail', 'hotel', 'restaurant', 'real-estate', 'professional-services', 'travel', 'education']
  },
  'support-maintenance': {
    id: 'support-maintenance',
    title: 'Support & Maintenance',
    icon: 'Shield',
    tagline: 'Reliable cloud hosting support, security updates, and performance checks.',
    overview: 'A website or app requires constant maintenance to remain secure and fast. We do not disappear after launch. We offer affordable, direct developer support to keep your software updated, handle hosting, check for database errors, and implement small feature changes. Think of us as your external technical department—honest help is always just a quick message away.',
    problems: [
      'Your website going down unexpectedly, and you have no developer to call to get it back online.',
      'Worries about data leaks, hackers, or outdated packages compromising client database security.',
      'Slow performance creep as your system files gather weight and legacy logs fill up.',
      'Struggling to deal with cloud server configuration, DNS settings, or SSL certificates.'
    ],
    solutions: [
      'We set up automated uptime monitoring and immediate alerts to fix server issues before clients notice.',
      'We perform regular security sweeps, update backend libraries, and check database backups.',
      'We manage DNS settings, SSL renewals, and cloud deployment pipelines on AWS or Vercel.',
      'We dedicate monthly hours to implement small updates, text edits, or image updates as you need them.'
    ],
    benefits: [
      'Peace of mind knowing a developer is monitor-guarding your site uptime.',
      'Secure customer data through automated backups and regular security library updates.',
      'Consistent fast performance through regular file cleanup and optimization.',
      'Direct, direct support—no corporate support tickets or endless automated hold queues.'
    ],
    features: [
      '24/7 Uptime Monitoring: Real-time checks with automatic alerts sent directly to our phones.',
      'Cloud & Hosting Setup: Configuration for secure servers on AWS, Vercel, Netlify, or Hostinger.',
      'Database Backup Management: Scheduled automatic backups of client and transaction records.',
      'Monthly Updates & Improvements: Dedicated support hours for text updates, layout changes, and code tweaks.'
    ],
    suitableIndustries: ['healthcare', 'retail', 'education', 'restaurant', 'hotel', 'real-estate', 'construction', 'manufacturing', 'travel', 'professional-services']
  }
};
